import {
  Anchor,
  Box,
  ButtonProps,
  Container,
  Group,
  List,
  Stepper,
  Text,
  Title,
} from "@mantine/core"
import Link from "next/link"
import { QualifyButton } from "./QualifyButton"
import { ThemeWrapper } from "./ThemeWrapper"
import { brand } from "~/brand"
import { Header } from "./Header"
import { Footer } from "./Footer"
import { useScrollIntoView } from "@mantine/hooks"

const CrossPageQualifyButton = (props: ButtonProps<"a">) => (
  <Link href="/#prequal" passHref>
    <QualifyButton {...props} />
  </Link>
)

export const HowItWorks = () => {
  const { targetRef: targetRefProcess, scrollIntoView: scrollIntoViewProcess } =
    useScrollIntoView<HTMLHeadingElement>()
  const { targetRef: targetRefSteps, scrollIntoView: scrollIntoViewSteps } =
    useScrollIntoView<HTMLHeadingElement>()
  const { targetRef: targetRefTimeline, scrollIntoView: scrollIntoViewTimeline } =
    useScrollIntoView<HTMLHeadingElement>()
  const { targetRef: targetRefProtected, scrollIntoView: scrollIntoViewProtected } =
    useScrollIntoView<HTMLHeadingElement>()

  return (
    <ThemeWrapper>
      <Header qualifyButton={CrossPageQualifyButton} />

      <Box sx={{ backgroundColor: brand.theme.yellow }} py="xl">
        <Container>
          <Text component="h1" weight={800} sx={{ fontSize: "52px" }}>
            How it works
          </Text>
          <Title order={3} sx={{ fontWeight: "normal", fontSize: "24px" }} mb="xl">
            Click on the different section below to learn more about our process to unlock your
            pension plan.
          </Title>

          <List sx={{ borderLeft: "10px solid white" }} pl="xl">
            <List.Item>
              <Anchor size="lg" color="dark" onClick={() => scrollIntoViewProcess()}>
                How does the process work?
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor size="lg" color="dark" onClick={() => scrollIntoViewSteps()}>
                What are the steps from start to finish?
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor size="lg" color="dark" onClick={() => scrollIntoViewTimeline()}>
                How long does the process take?
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor size="lg" color="dark" onClick={() => scrollIntoViewProtected()}>
                Are my funds protected?
              </Anchor>
            </List.Item>
          </List>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: brand.theme.darkblue }} p={30}>
        <Container>
          <Title
            ref={targetRefProcess}
            order={2}
            sx={{ color: brand.theme.yellow, fontSize: "50px", fontWeight: "normal" }}
            mt="xl"
            mb="xl"
          >
            Can you explain how the process works?
          </Title>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            We have partnerships with various financial institutions that we use as a part of this
            process. They are all regulated by the Office of the Superintendent of Financial
            Institutions (OFSI) which is a government agency that regulates financial institutions
            such as banks, credit unions, and trust companies. Your funds are 100% protected by the
            Canadian Deposit Insurance Corporation (CDIC).
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            If your Locked-in account (LIRA, LRSP, LIF) is not with one of the institutions that we
            use, we will need to open a new locked-in account for you with one of our partners.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            The role we play in this transaction is that we are a lead generation referral site. In
            other words, we are the middleman between you and the financial institutions able to
            assist you in accessing your funds. It is our job to confirm that your plan qualifies
            for this transaction, and if so to match you with the best suited Canadian Financial
            Services Provider. We are also responsible for being your main contact through the
            process and coordinating everything from A to Z between all the different institutions
            involved.
          </Text>

          <Title
            ref={targetRefSteps}
            order={2}
            sx={{ color: brand.theme.yellow, fontSize: "50px", fontWeight: "normal" }}
            mt={60}
            mb="xl"
          >
            What are the steps from start to finish?
          </Title>

          <Stepper
            active={0}
            orientation="vertical"
            styles={(theme) => ({
              stepIcon: {
                border: "none",
                backgroundColor: theme.colors.cyan[4],
                color: "white",
              },
            })}
          >
            <Stepper.Step
              label={
                <Group>
                  <Text size="lg" color="white">
                    Complete the “do you qualify” form.
                  </Text>
                  <CrossPageQualifyButton />
                </Group>
              }
            />

            <Stepper.Step
              label={
                <Text size="lg" color="white">
                  If you qualify, have a chat with one of our agent to explain to you the process
                  and fees.
                </Text>
              }
            />
            <Stepper.Step
              label={
                <Text size="lg" color="white">
                  Complete the official application and get an advance right away if you need one!
                </Text>
              }
            />
            <Stepper.Step
              label={
                <Text size="lg" color="white">
                  Get your funds deposited into your account.
                </Text>
              }
            />
          </Stepper>

          <Title
            ref={targetRefTimeline}
            order={2}
            sx={{ color: brand.theme.yellow, fontSize: "50px", fontWeight: "normal" }}
            mt={60}
            mb="xl"
          >
            How long does the process take?
          </Title>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            The process takes anywhere from 2 to 6 weeks depending on what type of plan you have and
            with which financial institution your plan is.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            Don&apos;t forget that we are more than happy to provide you with an advance if you have
            an immediate financial emergency to address. ​
          </Text>

          <Title
            ref={targetRefProtected}
            order={2}
            sx={{ color: brand.theme.yellow, fontSize: "50px", fontWeight: "normal" }}
            mt={60}
            mb="xl"
          >
            Are my funds protected?
          </Title>

          <Text size="lg" mb="xl" weight="bold" sx={{ color: "white" }}>
            How do I know that with this I will not lose my money?
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            Are my funds protected? How do I know that with this I will not lose my money? As
            mentioned before, all our Financial Institution Partners are properly registered and
            certified with the relevant and applicable government agencies. Your funds are also
            protected under the Canadian Deposit Insurance Corporation (CDIC).
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            Aside from this, there are several checks and balances in the process to ensure the
            safety of your funds, starting with your current financial institution. They will not
            transfer your plan unless the receiving institution is legally allowed to operate as an
            entity that may receive locked-in/registered plans.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            The funds are transferred into a new account registered in your name. Therefore, once
            your funds are transferred, no one can access your funds or have them transferred or
            released without your consent.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            You have the capacity to cancel the transaction at any time prior to receiving your
            final deposit. Basically, until the funds are released to you, you still have the right
            to terminate this agreement and have the funds returned to your original institution.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            Additionally, there are severe fines and potential loss of licenses for all the
            institutions involved in the process. As you can assume, this would be a critical blow
            to all the companies involved and we would not operate in a capacity that would lead to
            this.
          </Text>

          <Text size="lg" mb="xl" sx={{ color: "white" }}>
            Finally, you have us as another layer of protection. Since we are the broker/referral
            service, <strong>we work for you</strong>. The last thing we want is an unsatisfied
            client or a client that was not 100% happy with our services. Our reputation is our
            resume. Poor reviews, complaints online, and a negative social media presence would
            destroy our reputation and therefore our business. We pride ourselves on processing each
            account with Integrity, Efficiency, and Transparency.
          </Text>
        </Container>
      </Box>

      <Footer qualifyButton={CrossPageQualifyButton} />
    </ThemeWrapper>
  )
}
